import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ApiService } from "../../api/api-service";

interface JourneyIssue {
  batch_no: string;
  journey_id: string;
  journeyDate: string;
  trainerName: string;
  issue_description: string;
  issue_status: string;
  createdAt: string;
  resolvedAt?: string;
  scheduleStatus: string;
  // resolve_comment?: string;
}

interface StaleBatch {
  batch_no: string;
  trainerName: string;
  courseName: string;
  admin: string;
  lastJourneyDate: string | null;
  lastMarkedAt: string | null;
  hasJourney: boolean;
  scheduleStatus: string; // ← added
}

const PAGE_SIZE = 15;

export default function JourneyIssuesDashboard() {
  const navigate = useNavigate();
  const [issues, setIssues] = useState<JourneyIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    batchNo: string;
    journeyId: string;
    comment: string;
  } | null>(null);

  // ── Main Table Filters ──
  const [batchFilter, setBatchFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");
  const [scheduleFilter, setScheduleFilter] = useState("");

  // ── Pagination ──
  const [currentPage, setCurrentPage] = useState(1);

  // ── Stale Journeys (Unmarked 24h+) ──
  const [showStale, setShowStale] = useState(false);
  const [staleBatches, setStaleBatches] = useState<StaleBatch[]>([]);
  const [staleLoading, setStaleLoading] = useState(false);
  const [stalePage, setStalePage] = useState(1);
  const [staleAdminFilter, setStaleAdminFilter] = useState("");
  const [staleScheduleFilter, setStaleScheduleFilter] = useState(""); // ← added

  // ── Derived options for main table schedule filter ──
  const scheduleOptions = Array.from(
    new Set(issues.map((i) => i.scheduleStatus).filter(Boolean)),
  ).sort() as string[];

  // ── Derived options for stale modal filters ──
  const staleAdminOptions = Array.from(
    new Set(staleBatches.map((b) => b.admin).filter(Boolean)),
  ).sort() as string[];

  const staleScheduleOptions = Array.from(
    new Set(staleBatches.map((b) => b.scheduleStatus).filter(Boolean)),
  ).sort() as string[];

  // ── Combined stale filter (admin + schedule) ──
  const combinedStaleBatches = staleBatches.filter((b) => {
    const adminMatch = staleAdminFilter
      ? b.admin?.toLowerCase().includes(staleAdminFilter.toLowerCase())
      : true;
    const scheduleMatch = staleScheduleFilter
      ? b.scheduleStatus === staleScheduleFilter
      : true;
    return adminMatch && scheduleMatch;
  });

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.getInstance().get("/api/journey-issues");
      setIssues(res.data.data || []);
    } catch (error) {
      console.error("Error fetching issues:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [batchFilter, adminFilter, scheduleFilter]);

  const fetchStaleBatches = useCallback(async () => {
    setStaleLoading(true);
    try {
      const res = await ApiService.getInstance().get("/api/stale-journeys");
      setStaleBatches(res.data.data || []);
    } catch (error) {
      console.error("Error fetching stale journeys:", error);
    } finally {
      setStaleLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (showStale) {
      await fetchStaleBatches();
    } else {
      await fetchIssues();
    }
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleToggleStale = async () => {
    const next = !showStale;
    setShowStale(next);
    setStalePage(1);
    setStaleAdminFilter("");
    setStaleScheduleFilter(""); // ← reset on close/open
    if (next && staleBatches.length === 0) {
      await fetchStaleBatches();
    }
  };

  const formatTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return "Never marked";
    const diff = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h ago`;
    return `${hours}h ago`;
  };

  const handleResolve = async (
    batchNo: string,
    journeyId: string,
    comment: string,
  ) => {
    setConfirmModal(null);
    setResolvingId(journeyId);
    try {
      await ApiService.getInstance().put(
        `/api/resolve-issue/${encodeURIComponent(batchNo)}/${encodeURIComponent(journeyId)}`,
        { resolve_comment: comment },
      );
      fetchIssues();
    } catch (error) {
      console.error("Error resolving issue:", error);
    } finally {
      setResolvingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // ── Derived state ──
  const openCount = issues.filter((i) => i.issue_status !== "resolved").length;
  const resolvedCount = issues.filter(
    (i) => i.issue_status === "resolved",
  ).length;

  const filteredIssues = issues.filter((issue) => {
    const batchMatch = batchFilter
      ? issue.batch_no.toLowerCase().includes(batchFilter.toLowerCase())
      : true;
    const adminMatch = adminFilter
      ? issue.trainerName?.toLowerCase().includes(adminFilter.toLowerCase())
      : true;
    const scheduleMatch = scheduleFilter
      ? issue.scheduleStatus === scheduleFilter
      : true;
    return batchMatch && adminMatch && scheduleMatch;
  });

  const totalPages = Math.ceil(filteredIssues.length / PAGE_SIZE);
  const paginatedIssues = filteredIssues.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const hasActiveFilters = batchFilter || adminFilter || scheduleFilter;

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* ── TOP HEADER ── */}
      <div className="bg-[#221E45] text-white px-8 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/Batches")}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 text-white border border-white/20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                Batch Journey Issues Dashboard
              </h1>
              <p className="text-xs text-white/60 mt-0.5">
                SuperAdmin view — Manage flagged journey issues
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-red-500/20 border border-red-400/40 rounded-full px-4 py-1.5">
                <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse"></span>
                <span className="text-sm font-semibold text-red-300">
                  {openCount} Open
                </span>
              </div>
              <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 rounded-full px-4 py-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5 text-emerald-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-sm font-semibold text-emerald-300">
                  {resolvedCount} Resolved
                </span>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 border border-white/20"
              title="Refresh"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-4 h-4 text-white ${refreshing ? "animate-spin" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* ── COMPACT STAT CARDS ── */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-5 py-3 shadow-sm">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800 leading-none">
                {issues.length}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Total Issues</p>
            </div>
          </div>

          <div className="w-px h-10 bg-slate-200" />

          <div className="flex items-center gap-3 bg-white rounded-xl border border-red-100 px-5 py-3 shadow-sm">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xl font-bold text-red-600 leading-none">
                {openCount}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Open Issues</p>
            </div>
          </div>

          <div className="w-px h-10 bg-slate-200" />

          <div className="flex items-center gap-3 bg-white rounded-xl border border-emerald-100 px-5 py-3 shadow-sm">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xl font-bold text-emerald-600 leading-none">
                {resolvedCount}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Resolved Issues</p>
            </div>
          </div>

          <div className="w-px h-10 bg-slate-200" />

          {/* Unmarked Journeys Toggle Button */}
          <button
            onClick={handleToggleStale}
            className={`flex items-center gap-3 rounded-xl border px-5 py-3 shadow-sm transition-all duration-200 cursor-pointer ${
              showStale
                ? "bg-amber-50 border-amber-300 ring-2 ring-amber-200"
                : "bg-white border-amber-100 hover:border-amber-300 hover:shadow-md"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                showStale ? "bg-amber-200" : "bg-amber-50"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-4 h-4 ${showStale ? "text-amber-700" : "text-amber-500"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p
                className={`text-xl font-bold leading-none ${
                  showStale ? "text-amber-700" : "text-amber-600"
                }`}
              >
                {staleBatches.length > 0 ? staleBatches.length : "—"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Unmarked 24h+</p>
            </div>
          </button>
        </div>

        {/* ── FILTER BAR ── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Label */}
            <div className="flex items-center gap-1.5 mr-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                />
              </svg>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Filters
              </span>
            </div>

            {/* Batch No Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search Batch No..."
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                className="pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 w-48 placeholder:text-slate-400 text-slate-700"
              />
              {batchFilter && (
                <button
                  onClick={() => setBatchFilter("")}
                  className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Schedule Status Filter */}
            <div className="relative">
              <select
                value={scheduleFilter}
                onChange={(e) => {
                  setScheduleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 text-slate-700 bg-white appearance-none min-w-[160px]"
              >
                <option value="">All Schedules</option>
                {scheduleOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* Clear All */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setBatchFilter("");
                  setAdminFilter("");
                  setScheduleFilter("");
                }}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Clear All
              </button>
            )}

            {/* Results count */}
            <div className="ml-auto">
              <span className="text-xs text-slate-400">
                {hasActiveFilters ? (
                  <>
                    <span className="font-semibold text-slate-600">
                      {filteredIssues.length}
                    </span>{" "}
                    of {issues.length} results
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-slate-600">
                      {issues.length}
                    </span>{" "}
                    total records
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* ── TABLE CARD ── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-8 h-8 border-[3px] border-[#221E45]/20 border-t-[#221E45] rounded-full animate-spin"></div>
              <p className="text-sm text-slate-400">Loading issues...</p>
            </div>
          ) : filteredIssues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-2">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-7 h-7 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-base font-semibold text-slate-600">
                No issues found
              </p>
              <p className="text-sm text-slate-400">
                {hasActiveFilters
                  ? "Try adjusting your filters"
                  : "All journey entries are clear!"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Batch No
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Journey Date
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Trainer
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Schedule
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5">
                      Issue Description
                    </th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Status
                    </th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedIssues.map((issue) => (
                    <tr
                      key={`${issue.batch_no}-${issue.journey_id}`}
                      className={`transition-colors duration-150 ${issue.issue_status === "resolved" ? "bg-white hover:bg-slate-50/60" : "bg-white hover:bg-red-50/30"}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-8 rounded-full bg-[#221E45]/20 flex-shrink-0"></div>
                          <span className="font-bold text-[#221E45] text-sm">
                            {issue.batch_no}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-slate-600">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3.5 h-3.5 text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="text-sm">
                            {formatDate(issue.journeyDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-[#221E45]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-[#221E45]">
                              {issue.trainerName
                                ? issue.trainerName.charAt(0).toUpperCase()
                                : "?"}
                            </span>
                          </div>
                          <span className="text-sm text-slate-700 font-medium">
                            {issue.trainerName || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {issue.scheduleStatus ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            {issue.scheduleStatus}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        {issue.issue_description &&
                        issue.issue_description !== "No description" ? (
                          <span className="text-sm text-slate-700 line-clamp-2">
                            {issue.issue_description}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400 italic">
                            No description provided
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        {issue.issue_status === "resolved" ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-3 h-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Resolved
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            Open
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        {issue.issue_status !== "resolved" ? (
                          <button
                            onClick={() =>
                              setConfirmModal({
                                open: true,
                                batchNo: issue.batch_no,
                                journeyId: issue.journey_id,
                                comment: "",
                              })
                            }
                            disabled={resolvingId === issue.journey_id}
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-200 shadow-sm hover:shadow disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {resolvingId === issue.journey_id ? (
                              <>
                                <svg
                                  className="w-3 h-3 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                  ></path>
                                </svg>
                                Resolving...
                              </>
                            ) : (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="w-3 h-3"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Resolve
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="flex flex-col items-center gap-1.5">
                            <div className="text-xs text-slate-400 flex items-center gap-1">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3 text-emerald-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              {issue.resolvedAt
                                ? formatDate(issue.resolvedAt)
                                : "Resolved"}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── PAGINATION FOOTER ── */}
          {!loading && filteredIssues.length > 0 && (
            <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}–
                  {Math.min(currentPage * PAGE_SIZE, filteredIssues.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700">
                  {filteredIssues.length}
                </span>{" "}
                records
              </p>

              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1,
                    )
                    .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1)
                        acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="w-8 h-8 flex items-center justify-center text-xs text-slate-400"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p as number)}
                          className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-colors ${
                            currentPage === p
                              ? "bg-[#221E45] text-white border-[#221E45]"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                          }`}
                        >
                          {p}
                        </button>
                      ),
                    )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── STALE JOURNEYS MODAL ── */}
      {showStale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowStale(false)}
          />

          {/* Modal Card */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-4 max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-amber-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-800">
                      Unmarked Journeys
                    </h2>
                    <p className="text-xs text-amber-600/80">
                      On-Going batches not marked in 24+ hours •{" "}
                      {staleBatches.length} batch
                      {staleBatches.length !== 1 ? "es" : ""} need attention
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowStale(false)}
                  className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-slate-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* ── SINGLE UNIFIED FILTER BAR (Admin + Schedule) ── */}
            {staleBatches.length > 0 && !staleLoading && (
              <div className="px-6 py-3 border-b border-amber-100 bg-white flex items-center gap-3 flex-shrink-0 flex-wrap">
                {/* Filter icon + label */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 text-slate-400 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                  />
                </svg>

                {/* Admin Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    Admin
                  </span>
                  <select
                    value={staleAdminFilter}
                    onChange={(e) => {
                      setStaleAdminFilter(e.target.value);
                      setStalePage(1);
                    }}
                    className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 text-slate-700 bg-white min-w-[150px]"
                  >
                    <option value="">All Admins</option>
                    {staleAdminOptions.map((admin) => (
                      <option key={admin} value={admin}>
                        {admin}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vertical divider */}
                <div className="w-px h-6 bg-slate-200" />

                {/* Schedule Status Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    Schedule
                  </span>
                  <select
                    value={staleScheduleFilter}
                    onChange={(e) => {
                      setStaleScheduleFilter(e.target.value);
                      setStalePage(1);
                    }}
                    className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 text-slate-700 bg-white min-w-[150px]"
                  >
                    <option value="">All Schedules</option>
                    {staleScheduleOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear button — shows only when either filter is active */}
                {(staleAdminFilter || staleScheduleFilter) && (
                  <button
                    onClick={() => {
                      setStaleAdminFilter("");
                      setStaleScheduleFilter("");
                      setStalePage(1);
                    }}
                    className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg px-2.5 py-1.5 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3 h-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Clear
                  </button>
                )}

                {/* Count */}
                <span className="ml-auto text-xs text-slate-400">
                  <span className="font-semibold text-slate-600">
                    {combinedStaleBatches.length}
                  </span>{" "}
                  of {staleBatches.length} batches
                </span>
              </div>
            )}

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto">
              {staleLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <div className="w-8 h-8 border-[3px] border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
                  <p className="text-sm text-slate-400">
                    Loading stale batches...
                  </p>
                </div>
              ) : staleBatches.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-2">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-8 h-8 text-emerald-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-base font-semibold text-slate-600">
                    All On-Going batches are up to date!
                  </p>
                  <p className="text-sm text-slate-400">
                    No batches have been unmarked for more than 24 hours
                  </p>
                </div>
              ) : combinedStaleBatches.length === 0 ? (
                /* No results after filtering */
                <div className="flex flex-col items-center justify-center py-20 gap-2">
                  <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-base font-semibold text-slate-600">
                    No batches match your filters
                  </p>
                  <p className="text-sm text-slate-400">
                    Try adjusting or clearing the filters above
                  </p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-amber-100 bg-amber-50/60">
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Batch No
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Course
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Trainer
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Schedule
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Last Journey
                      </th>
                      <th className="text-center py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {combinedStaleBatches
                      .slice((stalePage - 1) * PAGE_SIZE, stalePage * PAGE_SIZE)
                      .map((batch) => (
                        <tr
                          key={batch.batch_no}
                          className="hover:bg-amber-50/40 transition-colors"
                        >
                          <td className="py-3.5 px-6">
                            <span className="text-sm font-bold text-[#221E45]">
                              {batch.batch_no}
                            </span>
                          </td>
                          <td className="py-3.5 px-6">
                            <span className="text-sm text-slate-600">
                              {batch.courseName}
                            </span>
                          </td>
                          <td className="py-3.5 px-6">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-[#221E45]">
                                {batch.trainerName.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm text-slate-700">
                                {batch.trainerName}
                              </span>
                            </div>
                          </td>
                          <td className="py-3.5 px-6">
                            <span className="text-sm text-slate-500">
                              {batch.admin}
                            </span>
                          </td>
                          <td className="py-3.5 px-6">
                            {batch.scheduleStatus ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                {batch.scheduleStatus}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400 italic">
                                —
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-6">
                            <span className="text-sm text-slate-600">
                              {batch.lastJourneyDate ? (
                                formatDate(batch.lastJourneyDate)
                              ) : (
                                <span className="text-red-500 font-medium">
                                  No Journey
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="py-3.5 px-6 text-center">
                            <button
                              onClick={() => {
                                window.open(
                                  `/batches/${batch.batch_no}/journey`,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#221E45] hover:bg-[#221E45]/90 rounded-lg shadow-sm transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              Open Journey
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer — pagination */}
            {combinedStaleBatches.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-amber-100 bg-amber-50/30 flex-shrink-0">
                <span className="text-xs text-slate-400">
                  Showing {(stalePage - 1) * PAGE_SIZE + 1}–
                  {Math.min(stalePage * PAGE_SIZE, combinedStaleBatches.length)}{" "}
                  of {combinedStaleBatches.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setStalePage((p) => Math.max(1, p - 1))}
                    disabled={stalePage === 1}
                    className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 font-medium"
                  >
                    ← Prev
                  </button>
                  <span className="px-3 py-1.5 text-xs font-semibold text-slate-600">
                    {stalePage} /{" "}
                    {Math.ceil(combinedStaleBatches.length / PAGE_SIZE)}
                  </span>
                  <button
                    onClick={() =>
                      setStalePage((p) =>
                        Math.min(
                          Math.ceil(combinedStaleBatches.length / PAGE_SIZE),
                          p + 1,
                        ),
                      )
                    }
                    disabled={
                      stalePage >=
                      Math.ceil(combinedStaleBatches.length / PAGE_SIZE)
                    }
                    className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 font-medium"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CONFIRMATION MODAL ── */}
      {confirmModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setConfirmModal(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border-4 border-emerald-100 mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-7 h-7 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-center text-base font-bold text-slate-800 mb-1">
              Resolve this Issue?
            </h3>
            <p className="text-center text-sm text-slate-500 mb-1">
              Are you sure you want to mark this issue as resolved?
            </p>
            <p className="text-center text-xs text-slate-400 mb-4">
              Batch No:{" "}
              <span className="font-semibold text-[#221E45]">
                {confirmModal.batchNo}
              </span>
            </p>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Resolution Comment{" "}
                <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea
                rows={3}
                placeholder="e.g. Verified with trainer, issue fixed..."
                value={confirmModal.comment}
                onChange={(e) =>
                  setConfirmModal((prev) =>
                    prev ? { ...prev, comment: e.target.value } : prev,
                  )
                }
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 resize-none placeholder:text-slate-400 text-slate-700"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleResolve(
                    confirmModal.batchNo,
                    confirmModal.journeyId,
                    confirmModal.comment,
                  )
                }
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-colors"
              >
                Yes, Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
